// Firemní volno (3. 11. 2026, zadání Ládi). Zavřená celá firma na zvolené období,
// typicky Vánoce. Manager a CSM ho navrhnou, owner schválí, owner ho může zadat
// rovnou. Před odesláním server spočítá dopad (akce nahled), nic se nemění.
//
// Po schválení server (/api/firemni-volno, akce schvalit):
//   - zablokuje rezervace osobních, zkušebních tréninků i konzultací v období,
//   - existující rezervace pošle trenérům a nutriční jako úkol ke zrušení,
//   - zruší bootcamp lekce ve všech lokalitách (SMS klientkám, vstup zpět),
//   - prodlouží platnost permanentek o dny volna,
//   - zapíše klientkám volno (měsíční minimum).
// Role hlídá server, tady se jen podle role mění tlačítko.

import React, { useEffect, useState } from "react";
import { Plane, Search, Send, Check, CalendarOff, Users, Flame, CalendarPlus, MessageSquare, ClipboardList } from "lucide-react";
import { Modal, HlavickaOkna, Field, Stitek, Hlaska } from "./ui.jsx";
import { apiFetch } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";

function dm(iso) {
  const [, m, d] = String(iso || "").split("-");
  return d && m ? `${Number(d)}. ${Number(m)}.` : "";
}
function sklon(n, a, b, c) { return n === 1 ? a : n >= 2 && n <= 4 ? b : c; }

function RadekDopadu({ ikona: Ikona, nadpis, children }) {
  return (
    <div className="flex gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-deep"><Ikona className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-800">{nadpis}</div>
        <div className="mt-1 flex flex-wrap gap-1.5">{children}</div>
      </div>
    </div>
  );
}

export function NahledDopadu({ n }) {
  const r = n.rezervace || {}, b = n.bootcamp || {}, p = n.platnost || {};
  return (
    <div className="space-y-2">
      <RadekDopadu ikona={CalendarOff} nadpis="Rezervace se v období zablokují">
        <Stitek ton="znacka" velikost="mala">osobní a zkušební tréninky</Stitek>
        <Stitek ton="znacka" velikost="mala">nutriční konzultace</Stitek>
      </RadekDopadu>
      <RadekDopadu ikona={ClipboardList} nadpis={r.celkem ? `${r.celkem} ${sklon(r.celkem, "rezervace", "rezervace", "rezervací")} už v období je, půjdou jako úkol ke zrušení` : "V období zatím nikdo rezervaci nemá"}>
        {(r.lide || []).map((x) => (
          <Stitek key={x.jmeno} ton={x.nutricni ? "info" : "neutralni"} velikost="mala">{x.jmeno}, {x.pocet}</Stitek>
        ))}
      </RadekDopadu>
      <RadekDopadu ikona={Flame} nadpis={b.lekci ? `Bootcamp: zruší se ${b.lekci} ${sklon(b.lekci, "lekce", "lekce", "lekcí")}` : "Bootcamp: v období žádná lekce není"}>
        {b.lokality && b.lokality.map((l) => <Stitek key={l} ton="neutralni" velikost="mala">{l}</Stitek>)}
        {b.sms > 0 && <Stitek ton="pozor" velikost="mala" ikona={MessageSquare}>SMS {b.sms} {sklon(b.sms, "klientce", "klientkám", "klientkám")}</Stitek>}
      </RadekDopadu>
      <RadekDopadu ikona={CalendarPlus} nadpis={`Platnost se prodlouží o ${n.dni} ${sklon(n.dni, "den", "dny", "dní")}`}>
        <Stitek ton="ok" velikost="mala">bootcamp permanentky {p.bootcamp || 0}</Stitek>
        <Stitek ton="ok" velikost="mala">permanentky na osobní tréninky {p.osobni || 0}</Stitek>
        <Stitek ton="ok" velikost="mala">dětské permice {p.deti || 0}</Stitek>
      </RadekDopadu>
      <RadekDopadu ikona={Users} nadpis="Klientkám se zapíše volno">
        <Stitek ton="neutralni" velikost="mala">{n.klientek || 0} aktivních klientek</Stitek>
        <Stitek ton="neutralni" velikost="mala">měsíční minimum se sníží</Stitek>
      </RadekDopadu>
      <RadekDopadu ikona={MessageSquare} nadpis="Informování klientek">
        <Stitek ton="info" velikost="mala">CSM: zprávy do Telegramu</Stitek>
        <Stitek ton="info" velikost="mala">trenéři a nutriční: říct svým klientkám</Stitek>
      </RadekDopadu>
      {n.sms_text && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">Text SMS klientkám bootcampu *</div>
          <div className="mt-1 text-sm text-slate-700">{n.sms_text}</div>
          <div className="mt-2 text-[12.5px] text-amber-900">* Pokud v období nejsou žádné lekce s přihlášenými klientkami, SMS se nepošle.</div>
        </div>
      )}
    </div>
  );
}

export default function FiremniVolnoDialog({ open, onClose, onHotovo }) {
  const jeOwner = getCurrentUser()?.role === "owner";
  const [od, setOd] = useState("");
  const [doDne, setDoDne] = useState("");
  const [nazev, setNazev] = useState("Vánoční volno");
  const [nahled, setNahled] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chyba, setChyba] = useState("");

  useEffect(() => { if (open) { setOd(""); setDoDne(""); setNazev("Vánoční volno"); setNahled(null); setChyba(""); setBusy(false); } }, [open]);

  const posli = async (akce) => {
    setBusy(true); setChyba("");
    try {
      const r = await apiFetch("/api/firemni-volno", { method: "POST", body: JSON.stringify({ akce, od, do: doDne || od, nazev: nazev.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || "Nepovedlo se, zkuste to prosím znovu.");
      return j;
    } catch (e) { setChyba(e.message); return null; } finally { setBusy(false); }
  };
  const nactiNahled = async () => { const j = await posli("nahled"); if (j) setNahled(j.nahled); };
  const odesli = async () => { const j = await posli(jeOwner ? "zalozit" : "navrhnout"); if (j) { onClose(); if (onHotovo) onHotovo(j); } };

  const zmena = (fn) => (e) => { fn(e.target.value); setNahled(null); };
  const lzeNahled = !!od && (!doDne || doDne >= od) && nazev.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} size="md" plnaHlavicka
      header={<HlavickaOkna ikona={Plane} nazev="Firemní volno" popis="Celá firma, všechny lokality" onClose={onClose} />}
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className="btn-secondary w-full sm:w-auto" onClick={onClose}>Zrušit</button>
          {!nahled ? (
            <button type="button" className="btn-primary w-full sm:w-auto" disabled={!lzeNahled || busy} onClick={nactiNahled}>
              <Search className="h-4 w-4" /> Zobrazit dopad
            </button>
          ) : (
            <button type="button" className="btn-primary w-full sm:w-auto" disabled={busy} onClick={odesli}>
              {jeOwner ? <><Check className="h-4 w-4" /> Schválit a provést</> : <><Send className="h-4 w-4" /> Odeslat ke schválení</>}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {chyba && <Hlaska ton="problem">{chyba}</Hlaska>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Od"><input type="date" className="field" value={od} onChange={zmena(setOd)} /></Field>
          <Field label="Do"><input type="date" className="field" value={doDne} min={od || undefined} onChange={zmena(setDoDne)} /></Field>
        </div>
        <Field label="Název" hint="Uvidí ho tým v kalendáři a v úkolech">
          <input className="field" value={nazev} maxLength={80} onChange={zmena(setNazev)} />
        </Field>
        {nahled ? (
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Dopad</span>
              <Stitek ton="znacka" velikost="mala">{dm(nahled.od)} až {dm(nahled.do)}</Stitek>
              <Stitek ton="znacka" velikost="mala">{nahled.dni} {sklon(nahled.dni, "den", "dny", "dní")}</Stitek>
            </div>
            <NahledDopadu n={nahled} />
            <Hlaska ton="pozor" className="mt-3" title={jeOwner ? "Schválením se volno hned provede" : "Volno se provede až po schválení ownerem"}>
              Schválené volno už nejde vrátit.
            </Hlaska>
          </div>
        ) : (
          <div className="text-[13px] text-slate-500">Po zadání období se ukáže, čeho se volno dotkne. Zatím se nic nemění.</div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Čekající návrhy firemního volna (owner). Každý návrh ukazuje, kdo ho poslal,
 * a dopad spočítaný znovu při načtení (data se mezitím mohla změnit).
 */
export function FiremniVolnoZadosti({ zadosti = [], onSchvalit, onZamitnout, busyId }) {
  if (!zadosti.length) return null;
  return (
    <div className="space-y-3">
      {zadosti.map((z) => (
        <div key={z.id} className="rounded-2xl border-2 border-amber-300 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-600"><Plane className="h-5 w-5" /></span>
            <span className="text-base font-bold text-slate-900">{z.nazev}</span>
            <Stitek ton="pozor" velikost="mala">čeká na schválení</Stitek>
            <Stitek ton="znacka" velikost="mala">{dm(z.od)} až {dm(z.do)}</Stitek>
            <Stitek ton="znacka" velikost="mala">{z.nahled.dni} {sklon(z.nahled.dni, "den", "dny", "dní")}</Stitek>
          </div>
          <div className="mt-1 text-[13px] text-slate-500">Navrhl(a) {z.navrhl}, {z.kdy}</div>
          <div className="mt-3"><NahledDopadu n={z.nahled} /></div>
          <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" className="btn-secondary w-full sm:w-auto" disabled={busyId === z.id} onClick={() => onZamitnout && onZamitnout(z)}>Zamítnout</button>
            <button type="button" className="btn-primary w-full sm:w-auto" disabled={busyId === z.id} onClick={() => onSchvalit && onSchvalit(z)}>
              <Check className="h-4 w-4" /> Schválit a provést
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
